import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { NavItem } from '../../NavItem';
import {SystemIcons} from '../../icons';
import { COLORS, BREAKPOINTS } from '../../styles';
import { Menu } from './ExtendedMainMenu';
import { SubMenu } from './SubMenu';
import { IconButton } from '../../Button';
import { defaultOnMouseDownHandler } from '../../common';
import { MenuNavigationItemTypeDesktopGroup, MenuNavigationItemTypeItem } from '../types';
import { NavItemDiv } from '../../NavItem/NavItem';
import { checkIfContainsItem } from '../utils';

const NavContainer = styled.div`
  display: none;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: center;
  
  padding: 0 16px;

  ${BREAKPOINTS.MEDIUM} {
    display: flex;
  }
  
  ${NavItemDiv} {
    cursor: pointer;
  }
  ${BREAKPOINTS.LARGE} {
    padding: 0 32px;
  }

  a {
    text-decoration: none;
  }
`;

const NavButton = styled.button`
  background: ${COLORS.white};
  border: 0;
`;

interface ExtendedMenuWrapperProps {
  floatRight: boolean;
}

const ExtendedMenuWrapper = styled.div<ExtendedMenuWrapperProps>`
  display: flex;
  margin-left: ${(props) => (props.floatRight ? 'auto' : 'unset')};

  position: absolute;
  flex-direction: column;
  left: 290px;
  top: 22px;

  height: 0;
  width: 0;

  &:focus-within,
  &:hover,
  &.open {
    background: ${COLORS.white};
  }
`;

const ExtendedSection = styled.div`
  position: relative;
`;

const SubMenuWrapper = styled.div<{ topLevel: boolean }>`
  ${Menu} {
    top: ${(props) => (props.topLevel ? '100%' : '0px')};
    left: ${(props) => (props.topLevel ? '0px' : 'calc(100% - 12px)')};
  }

  button {
    padding-left: 0px;
  }
`;

const ExtendedMenuOption = 'extendedOption';

type Props = {
  items: (MenuNavigationItemTypeItem | MenuNavigationItemTypeDesktopGroup)[];
  rightSideRef?: React.RefObject<HTMLDivElement> | null | undefined;
};

const MainMenu = ({ items, rightSideRef }: Props): React.ReactElement<Props> => {
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  //we have to use refs instead of 'state' here, because when onmouseleave is triggered for multiple nested divs, state update is not being updated in between
  const dropdownMenusOpenedArray = React.useRef<string[]>([]);
  const mainMenuRef: React.RefObject<HTMLDivElement> | null | undefined = React.useRef(null);
  const [numberOfItemsAllowed, setNumberOfItemsAllowed] = React.useState<number>(1);
  const [subMenuRefs, setAllSubMenuRefs] = React.useState<(React.RefObject<HTMLUListElement> | undefined)[]>([]);
  const extendedMenuOptionRef = React.createRef<HTMLUListElement>();

  React.useEffect(() => {
    setAllSubMenuRefs(items.map((x) => (x.type == 'desktopgroup' ? React.createRef<HTMLUListElement>() : undefined)));
  }, [items]);

  const [, forceUpdate] = React.useState({});
  const [activeItem, setActiveItemId] = React.useState<string>('');
  const [focusedItemId, setFocusedItemId] = React.useState<string>('');

  const widthOfItem = 120;
  const recalculateNumberOfItemsAllowed = () => {
    if (rightSideRef?.current && mainMenuRef?.current) {
      const number = Math.floor((rightSideRef?.current?.offsetLeft - mainMenuRef?.current?.offsetLeft) / widthOfItem);
      if (number !== numberOfItemsAllowed) {
        setNumberOfItemsAllowed(number);
      }
    }
  };

  const onMouseLeave = (event: any, id: string) => {
    dropdownMenusOpenedArray.current = [...dropdownMenusOpenedArray.current.filter((x) => x != id)];
    //just trigger rerendering
    forceUpdate({});
  };

  const onMouseEnter = (event: any, id: string) => {
    const existingRefForId = dropdownMenusOpenedArray.current.find((x) => x == id);
    if (existingRefForId == null) {
      dropdownMenusOpenedArray.current = [...dropdownMenusOpenedArray.current, id];
      //just trigger rerendering
      forceUpdate({});
    }
  };

  React.useEffect(() => {
    recalculateNumberOfItemsAllowed();
    // Bind the event listener
    window.addEventListener('resize', recalculateNumberOfItemsAllowed);
    return () => {
      // Unbind the event listener on clean up
      window.removeEventListener('resize', recalculateNumberOfItemsAllowed);
    };
  }, []);

  React.useEffect(() => {
    recalculateNumberOfItemsAllowed();
  }, [items.length]);

  const onFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    //no open dropdowns if top level was entered
    dropdownMenusOpenedArray.current = [];
    forceUpdate({});
  };

  const onKeyDownTop = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      dropdownMenusOpenedArray.current = [];
      //just trigger rerendering
      forceUpdate({});
    }
  };

  const renderItem = (item: MenuNavigationItemTypeItem | MenuNavigationItemTypeDesktopGroup, index: number) => {
    if (index > numberOfItemsAllowed - 2) return null;
    if (item.type != 'desktopgroup') {
      const { icon, to, exact, disabled, external, action, label, note, pinned, ...rest } = item as MenuNavigationItemTypeItem;
      return item?.disabled ? (
        <NavItem as={NavButton} className="disabled" key={item.label} onMouseDown={defaultOnMouseDownHandler}>
          {item?.label}
        </NavItem>
      ) : (
        <NavItem as={NavLink} onFocus={onFocus} to={to || ''} key={label} onMouseDown={defaultOnMouseDownHandler} onClick={(e: React.MouseEvent) => action && action(e)} {...rest}>
          {item?.label}
        </NavItem>
      );
    } else {
      const existingRef = dropdownMenusOpenedArray.current.find((x) => x == item.id);
      const onClick = () => {
        if (existingRef == null) {
          dropdownMenusOpenedArray.current = [...dropdownMenusOpenedArray.current, item.id];
          //just trigger rerendering
          forceUpdate({});
        }
      };
      const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key == 'Enter' || event.code == 'Space') {
          dropdownMenusOpenedArray.current = [...dropdownMenusOpenedArray.current, item.id];
          //just trigger rerendering
          forceUpdate({});
        } 
      };

      const containsActiveItem = checkIfContainsItem(item, activeItem);
      return (
        <div
          style={{ position: 'relative' }}
          onMouseEnter={(event) => onMouseEnter(event, item.id ?? '')}
          onMouseLeave={(event) => onMouseLeave(event, item.id ?? '')}>
          <NavItem
            id={item.id + '_parentOption'}
            aria-expanded={Boolean(existingRef)}
            aria-activedescendant={containsActiveItem ? activeItem : ''}
            aria-controls={item.id}
            role="listbox"
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            tabIndex={0}
            className={existingRef != null || checkIfContainsItem(item, activeItem) ? 'active' : ''}
            key={item.label}
            to={null}
            onClick={onClick}>
            {item?.label} <SystemIcons.ArrowDropDown size="24px" />
          </NavItem>
          <SubMenu
            ariaLabelledBy={item.id + '_parentOption'}
            ref={subMenuRefs[index]}
            onItemFocused={setFocusedItemId}
            focusedItemId={focusedItemId}
            activeItemId={activeItem}
            nestPath={[item.id]}
            onItemStateChanged={(item, state) => {
              if (state) {
                setActiveItemId(item);
              } else if (item == activeItem) setActiveItemId('');
            }}
            item={item as MenuNavigationItemTypeDesktopGroup}
            dropdownMenusOpenedArray={dropdownMenusOpenedArray}
            index={index + 1}
            topLevel={true}
          />
        </div>
      );
    }
  };


  const extended = dropdownMenusOpenedArray.current.find((x) => x == ExtendedMenuOption);
  const extendedSectionKeyHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key == 'Enter' || event.code == 'Space') {
      event.stopPropagation();

      dropdownMenusOpenedArray.current = [ExtendedMenuOption];
      forceUpdate({});
    }
  };

  return (
    <NavContainer role="navigation" className="GlobalNavigationMainMenu" ref={mainMenuRef} onKeyDown={onKeyDownTop}>
      {items?.map(renderItem)}
      {items.length + 1 > numberOfItemsAllowed && (
        <ExtendedSection
          onKeyDown={(event) => extendedSectionKeyHandler(event)}
          onMouseEnter={(event) => onMouseEnter(event, ExtendedMenuOption)}
          onMouseLeave={(event) => onMouseLeave(event, ExtendedMenuOption)}>
          <IconButton
            id={ExtendedMenuOption + '_parentOption'}
            aria-expanded={Boolean(extended)}
            aria-activedescendant={checkIfContainsItem({ items: items.slice(numberOfItemsAllowed - 1), id: ExtendedMenuOption }, activeItem) ? activeItem : ''}
            aria-controls={ExtendedMenuOption}
            role="listbox"
            
            ref={menuButtonRef} variant={'secondary'} shape={'circular'} action={() => {}}>
            <SystemIcons.MoreHorizontal size="24px"/>
          </IconButton>
          <SubMenu
            ariaLabelledBy={ExtendedMenuOption + '_parentOption'}
            activeItemId={activeItem}
            onItemFocused={setFocusedItemId}
            focusedItemId={focusedItemId}
            ref={extendedMenuOptionRef}
            onItemStateChanged={(item, state) => {
              if (state) setActiveItemId(item);
              else if (item == activeItem) setActiveItemId('');
            }}
            nestPath={[ExtendedMenuOption]}
            item={{ items: items.slice(numberOfItemsAllowed - 1), id: ExtendedMenuOption }}
            topLevel={true}
            index={0}
            dropdownMenusOpenedArray={dropdownMenusOpenedArray}
          />
        </ExtendedSection>
      )}
    </NavContainer>
  );
};

export default MainMenu;
